import { useState, useEffect, useMemo, useRef } from 'react';
import { LogEntry } from '../lib/supabase';
import { LogEntryItem } from '../components/cars/LogEntryItem';
import { getExpenseTypeNameFromData, normalizeExpenseTypeKey } from '../lib/expenseTypeHelpers';
import { MissingMileageModal } from '../components/cars/MissingMileageModal';
import { DynamicIcon } from '../lib/icons';
import { Rocket, ChevronDown, ChevronRight, TrendingUp, TrendingDown, Minus, Plus } from 'lucide-react';
import { usePreferences } from '../contexts/PreferencesContext';
import { formatCurrency } from '../lib/conversions';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import { useOutletContext, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from '../i18n';
import { LOG_ENTRY_KEYS } from '../hooks/useCarData';
import { estimateMileageAtDate } from '../lib/mileageUtils';
import defaultExpenseTypesData from '../data/defaultExpenseTypes.json';

type StatisticsProps = {
  logEntries: LogEntry[];
  selectedYear: number | 'all';
};

type TypeStats = {
  type: string;
  total: number;
  count: number;
  percentage: number;
  topEntries: LogEntry[];
  entryMetadata: Record<string, { amount: number; description: string }>;
  totalLiters?: number;
  totalElectricityKwh?: number;
  avgUnitPrice?: number;
  minUnitPrice?: number;
  maxUnitPrice?: number;
  color: string;
  icon: string;
  isService: boolean;
  groupTotal: number;
  groupPercentage: number;
  groupName: string;
};

function ChartTeaser({ title }: { title: string }) {
  const { t } = useTranslations();
  return (
    <div className="relative h-[240px] w-full flex flex-col items-center justify-center bg-gray-50/50 dark:bg-white/[0.02] rounded-2xl border border-dashed border-gray-200 dark:border-white/10 group overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-red-500/[0.02] to-red-500/[0.05] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mb-4 relative shadow-sm">
        <Rocket size={24} className="animate-pulse" />
      </div>
      <div className="text-center px-6 relative z-10">
        <p className="text-sm font-black text-gray-800 dark:text-gray-100 uppercase tracking-widest mb-1.5 line-clamp-1">
          {title}
        </p>
        <p className="text-xs font-medium text-gray-400 dark:text-gray-500 mb-5 leading-relaxed max-w-[220px] mx-auto line-clamp-2 italic">
          {(t.statistics as any).proStatsFeatureDesc || 'Unlock advanced analysis and historical trends.'}
        </p>
        <Link 
          to="/settings/plans" 
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 dark:bg-red-500 text-white text-xs font-black rounded-xl hover:bg-red-700 dark:hover:bg-red-400 transition-all shadow-lg shadow-red-500/20 active:scale-95"
        >
          <Rocket size={14} />
          {(t.statistics as any).upgradeToSee || 'Uzlabot →'}
        </Link>
      </div>
    </div>
  );
}

export function Statistics2({ logEntries, selectedYear }: StatisticsProps) {
  const [expandedType, setExpandedType] = useState<string | null>(() => {
    return sessionStorage.getItem('stats_expandedType');
  });

  useEffect(() => {
    if (expandedType === null) sessionStorage.removeItem('stats_expandedType');
    else sessionStorage.setItem('stats_expandedType', expandedType);
  }, [expandedType]);

  const [activePieIndex, setActivePieIndex] = useState<number | null>(null);
  const [showMileageModal, setShowMileageModal] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { preferences } = usePreferences();
  const { t, language } = useTranslations();
  const { ownerPreferences, selectedCar, isReadOnly, isAdminView } = useOutletContext<any>() || {};

  const isPro = true; // Temporary allow everyone (actual logic: ownerPreferences?.plan_type ? ownerPreferences.plan_type !== 'free' : false)
  const isRestricted = !isPro && !isAdminView;

  const effectivePreferences = (ownerPreferences || preferences) ? {
    ...(ownerPreferences || preferences),
    distance_unit: selectedCar?.distance_unit || (ownerPreferences || preferences).distance_unit,
    volume_unit: selectedCar?.volume_unit || (ownerPreferences || preferences).volume_unit,
    currency: selectedCar?.currency || (ownerPreferences || preferences).currency,
    currency_symbol: selectedCar?.currency_symbol || (ownerPreferences || preferences).currency_symbol,
  } : null;

  const fmt = (val: number) =>
    effectivePreferences
      ? formatCurrency(val, effectivePreferences.currency, effectivePreferences.currency_symbol)
      : `€${val.toFixed(2)}`;

  const getTypeName = (type: string) => {
    const key = normalizeExpenseTypeKey(type);
    const info = (defaultExpenseTypesData as any)[key];
    if (info) return getExpenseTypeNameFromData(info, language as any);
    return (t.expenses.typeLabels as any)[type] || type;
  };

  const getTypeInfo = (type: string) => {
    const key = normalizeExpenseTypeKey(type);
    return (defaultExpenseTypesData as any)[key] || { icon: 'Circle', color: '#6B7280' };
  };


  const flatExpenses = useMemo(() => {
    return logEntries.flatMap(entry => (entry.expenses || []).map(exp => ({
      ...exp,
      entry, // Keep reference to parent entry for LogEntryItem and date filtering
      date: entry.date
    })));
  }, [logEntries]);


  const filteredExpenses = useMemo(
    () => selectedYear === 'all' ? flatExpenses : flatExpenses.filter(e => new Date(e.date).getFullYear() === selectedYear),
    [flatExpenses, selectedYear]
  );

  const totalExpenses = useMemo(
    () => filteredExpenses.filter(e => e.type !== 'info').reduce((sum, e) => sum + (e.amount ? parseFloat(e.amount.toString()) : 0), 0),
    [filteredExpenses]
  );

  // Previous year total for trend comparison
  const prevYearTotal = useMemo(() => {
    if (selectedYear === 'all') return null;
    const prevYear = (selectedYear as number) - 1;
    return flatExpenses
      .filter(e => new Date(e.date).getFullYear() === prevYear && e.type !== 'info')
      .reduce((sum, e) => sum + (e.amount ? parseFloat(e.amount.toString()) : 0), 0);
  }, [flatExpenses, selectedYear]);

  const typeStats: TypeStats[] = useMemo(() => {
    const map: Record<string, {
      type: string;
      total: number;
      count: number;
      entryMap: Record<string, {
        entry: LogEntry,
        categoryTotal: number,
        categoryDescriptions: string[]
      }>;
      unitPrices: number[];
      totalLiters: number;
      totalElectricityKwh: number;
      color: string;
      icon: string;
    }> = {};

    filteredExpenses.forEach(expense => {
      if (expense.type === 'info') return;

      const key = normalizeExpenseTypeKey(expense.type);
      const info = getTypeInfo(expense.type);

      if (!map[key]) {
        map[key] = {
          type: key,
          total: 0,
          count: 0,
          entryMap: {},
          unitPrices: [],
          totalLiters: 0,
          totalElectricityKwh: 0,
          color: info.color || '#6B7280',
          icon: info.icon || 'Circle'
        };
      }

      const amount = parseFloat(expense.amount?.toString() || '0');
      map[key].total += amount;
      map[key].count += 1;

      const entry = (expense as any).entry as LogEntry;
      if (entry) {
        if (!map[key].entryMap[entry.id]) {
          map[key].entryMap[entry.id] = { entry, categoryTotal: 0, categoryDescriptions: [] };
        }
        map[key].entryMap[entry.id].categoryTotal += amount;
        if (expense.description?.trim()) {
          map[key].entryMap[entry.id].categoryDescriptions.push(expense.description.trim());
        }
      }

      if (expense.type === 'fuel' && typeof expense.fuel_liters === 'number' && expense.fuel_liters > 0) {
        const liters = expense.fuel_liters;
        map[key].totalLiters += liters;
        map[key].unitPrices.push((amount || 0) / liters);
      }
      if (expense.type === 'electricity' && typeof expense.electricity_kwh === 'number' && expense.electricity_kwh > 0) {
        const kwh = expense.electricity_kwh;
        map[key].totalElectricityKwh += kwh;
        map[key].unitPrices.push((amount || 0) / kwh);
      }
    });

    const stats = Object.values(map)
      .map(s => {
        const info = (defaultExpenseTypesData as any)[s.type] || {};
        const isService = info.service_group === true || s.type === 'repair';
        const avg = s.unitPrices.length > 0 ? s.unitPrices.reduce((a, b) => a + b, 0) / s.unitPrices.length : undefined;
        const min = s.unitPrices.length > 0 ? Math.min(...s.unitPrices) : undefined;
        const max = s.unitPrices.length > 0 ? Math.max(...s.unitPrices) : undefined;

        return {
          ...s,
          isService,
          percentage: totalExpenses > 0 ? (s.total / totalExpenses) * 100 : 0,
          avgUnitPrice: avg,
          minUnitPrice: min,
          maxUnitPrice: max,
          topEntries: Object.values(s.entryMap)
            .sort((a, b) => b.categoryTotal - a.categoryTotal)
            .slice(0, 10)
            .map(item => item.entry),
          entryMetadata: Object.fromEntries(
            Object.entries(s.entryMap).map(([id, data]) => [
              id,
              {
                amount: data.categoryTotal,
                description: data.categoryDescriptions.join(', ')
              }
            ])
          )
        };
      });

    // Calculate group totals for services
    const serviceTotal = stats.filter(s => s.isService).reduce((sum, s) => sum + s.total, 0);
    const servicePercentage = totalExpenses > 0 ? (serviceTotal / totalExpenses) * 100 : 0;

    return stats.map(s => {
      const serviceLabel = (t.expenses.typeLabels as any)?.service || (language === 'lv' ? 'Serviss' : 'Service');
      return {
        ...s,
        groupTotal: s.isService ? serviceTotal : s.total,
        groupPercentage: s.isService ? servicePercentage : s.percentage,
        groupName: s.isService ? serviceLabel : getTypeName(s.type)
      };
    })
    .sort((a, b) => b.total - a.total);
  }, [filteredExpenses, totalExpenses, language, t.expenses.typeLabels]);

  // Fuel price history for the new chart
  const fuelPriceData = useMemo(() => {
    return filteredExpenses
      .filter(e => e.type === 'fuel' && typeof e.fuel_liters === 'number' && e.fuel_liters > 0)
      .map(e => {
        const d = new Date(e.date);
        return {
          date: d.getTime(),
          displayDate: d.toLocaleDateString(language, {
            day: '2-digit',
            month: 'short',
            year: selectedYear === 'all' ? '2-digit' : undefined
          }),
          fullDate: d.toLocaleDateString(language, {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          }),
          price: (parseFloat(e.amount?.toString() || '0')) / (e.fuel_liters || 1)
        };
      })
      .sort((a, b) => a.date - b.date);
  }, [filteredExpenses, language, selectedYear]);

  // Monthly data for current + previous year (if year selected)
  const monthlyData = useMemo(() => {
    const map: Record<string, { label: string; total: number; year: number; monthIdx: number }> = {};
    filteredExpenses.filter(e => e.type !== 'info').forEach(e => {
      const d = new Date(e.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!map[key]) {
        map[key] = {
          label: d.toLocaleDateString(language, { month: 'short', year: selectedYear === 'all' ? '2-digit' : undefined }),
          total: 0,
          year: d.getFullYear(),
          monthIdx: d.getMonth(),
        };
      }
      if (e.amount) map[key].total += parseFloat(e.amount.toString());
    });
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => v);
  }, [filteredExpenses, selectedYear, language]);

  const avgMonthly = monthlyData.length > 0 ? totalExpenses / (selectedYear === 'all' ? monthlyData.length : 12) : totalExpenses;

  // Determine purchase year and purchase date (first ever log entry)
  const purchaseInfo = useMemo(() => {
    const allSorted = [...logEntries].sort((a, b) => {
      const d = a.date.localeCompare(b.date);
      if (d !== 0) return d;
      return (a.created_at || "").localeCompare(b.created_at || "");
    });
    if (allSorted.length === 0) return null;
    const firstEntry = allSorted[0];
    const firstYear = new Date(firstEntry.date).getFullYear();
    return { year: firstYear, date: firstEntry.date.split('T')[0] };
  }, [logEntries]);

  // Smart Distance Calculation for the selected period
  const distance = useMemo(() => {
    if (!selectedCar) return 0;
    
    let startDate: Date;
    let endDate: Date;
    
    if (selectedYear === 'all') {
      const dates = logEntries.map(e => new Date(e.date).getTime());
      if (dates.length === 0) return 0;
      startDate = new Date(Math.min(...dates));
      endDate = new Date(); // Today
    } else {
      startDate = new Date(selectedYear, 0, 1);
      endDate = new Date(selectedYear, 11, 31, 23, 59, 59);
      
      // If car was bought in this year, don't calculate distance before purchase
      if (selectedCar.bought_at) {
        const boughtDate = new Date(selectedCar.bought_at);
        if (boughtDate.getFullYear() === selectedYear) {
          startDate = boughtDate;
        }
      }
    }

    const estStart = estimateMileageAtDate(selectedCar.purchase_mileage, selectedCar.bought_at, logEntries, selectedCar.estimated_yearly_km, startDate);
    const estEnd = estimateMileageAtDate(selectedCar.purchase_mileage, selectedCar.bought_at, logEntries, selectedCar.estimated_yearly_km, endDate);
    
    return Math.max(0, estEnd - estStart);
  }, [logEntries, selectedYear, selectedCar]);

  const cpk = distance > 0 ? totalExpenses / distance : 0;

  const distUnit = effectivePreferences?.distance_unit === 'miles' ? t.settings.miles : t.settings.kilometers;

  // Yearly totals
  const yearlyData = useMemo(() => {
    const map: Record<number, number> = {};
    flatExpenses.filter(e => e.type !== 'info').forEach(e => {
      const y = new Date(e.date).getFullYear();
      map[y] = (map[y] || 0) + (e.amount ? parseFloat(e.amount.toString()) : 0);
    });
    return Object.entries(map)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([year, total]) => ({ year: Number(year), label: year, total }));
  }, [flatExpenses]);

  // Mileage history chart data
  const mileageData = useMemo(() => {
    return logEntries
      .filter(e => e.mileage && e.mileage > 0)
      .map(e => {
        const d = new Date(e.date);
        return {
          date: d.getTime(),
          displayDate: d.toLocaleDateString(language, {
            day: '2-digit',
            month: 'short',
            year: selectedYear === 'all' ? '2-digit' : undefined
          }),
          fullDate: d.toLocaleDateString(language, {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          }),
          mileage: e.mileage
        };
      })
      .sort((a, b) => a.date - b.date);
  }, [logEntries, language, selectedYear]);

  // Yearly CPK data to show when selectedYear is 'all'
  const yearlyCpkData = useMemo(() => {
    if (selectedYear !== 'all') return [];

    const mileagesAll = logEntries
      .filter(e => typeof e.mileage === 'number' && e.mileage > 0)
      .sort((a, b) => a.date.localeCompare(b.date));

    // Use the availableYears to generate one point per year
    const years = [...new Set(logEntries.map(e => new Date(e.date).getFullYear()))].sort((a, b) => a - b);

    return years.map(year => {
      const yearStart = `${year}-01-01`;
      const yearEnd = `${year}-12-31T23:59:59`;
      
      const expensesForYr = flatExpenses.filter(e => {
        const d = new Date(e.date).getFullYear();
        return d === year && e.type !== 'info';
      });

      const totalCost = expensesForYr.reduce((sum, e) => sum + (e.amount ? parseFloat(e.amount.toString()) : 0), 0);

      const startDate = new Date(year, 0, 1);
      const endDate = new Date(year, 11, 31, 23, 59, 59);
      
      let estYearStart = startDate;
      if (selectedCar?.bought_at) {
        const boughtDate = new Date(selectedCar.bought_at);
        if (boughtDate.getFullYear() === year) {
          estYearStart = boughtDate;
        }
      }

      const dist = selectedCar ? (estimateMileageAtDate(selectedCar.purchase_mileage, selectedCar.bought_at, logEntries, selectedCar.estimated_yearly_km, endDate) - 
                   estimateMileageAtDate(selectedCar.purchase_mileage, selectedCar.bought_at, logEntries, selectedCar.estimated_yearly_km, estYearStart)) : 0;

      const cpkValue = dist > 0 ? totalCost / dist : 0;
      console.log(`[STATS] Yearly data - Year: ${year}, cost: ${totalCost}, dist: ${dist}, cpk: ${cpkValue}`);

      return {
        year: year.toString(),
        yearNum: year,
        cpk: cpkValue,
        cost: totalCost,
        distance: dist
      };
    }).filter(d => d.distance > 0 || d.cost > 0);
  }, [logEntries, flatExpenses, selectedYear]);

  // Service trends chart data
  const serviceTrendData = useMemo(() => {
    const years = [...new Set(logEntries.map(e => new Date(e.date).getFullYear()))].sort((a, b) => a - b);
    
    const yearTotals = years.map(year => {
      const yearExpenses = flatExpenses.filter(e => new Date(e.date).getFullYear() === year);
      
      const repairs = yearExpenses
        .filter(e => normalizeExpenseTypeKey(e.type) === 'service_repair' || normalizeExpenseTypeKey(e.type) === 'repairs')
        .reduce((sum, e) => sum + (e.amount ? parseFloat(e.amount.toString()) : 0), 0);
        
      const maintenance = yearExpenses
        .filter(e => normalizeExpenseTypeKey(e.type) === 'service_maintenance' || normalizeExpenseTypeKey(e.type) === 'maintenance')
        .reduce((sum, e) => sum + (e.amount ? parseFloat(e.amount.toString()) : 0), 0);
        
      const upgrade = yearExpenses
        .filter(e => normalizeExpenseTypeKey(e.type) === 'service_tuning' || normalizeExpenseTypeKey(e.type) === 'parts')
        .reduce((sum, e) => sum + (e.amount ? parseFloat(e.amount.toString()) : 0), 0);
        
      return { year, repairs, maintenance, upgrade };
    });

    return yearTotals.map((curr, idx) => {
      const prev = idx > 0 && yearTotals[idx - 1].year === curr.year - 1 ? yearTotals[idx - 1] : null;
      
      const getTrend = (c: number, p: number | undefined) => {
        if (p === undefined || p === 0) return null;
        return ((c - p) / p) * 100;
      };

      return {
        ...curr,
        repairsTrend: getTrend(curr.repairs, prev?.repairs),
        maintenanceTrend: getTrend(curr.maintenance, prev?.maintenance),
        upgradeTrend: getTrend(curr.upgrade, prev?.upgrade)
      };
    });
  }, [logEntries, flatExpenses]);

  // Asset chart data (same logic as Statistics.tsx)
  const chartData = useMemo(() => {
    if (filteredExpenses.length === 0) return [];
    const allSorted = [...flatExpenses].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const valPoints = allSorted
      .filter(e => e.entry?.car_value && Number(e.entry.car_value) > 0)
      .map(e => ({ t: new Date(e.date).getTime(), v: Number(e.entry!.car_value) }));
    const displaySorted = filteredExpenses
      .filter(e => e.type !== 'info' || (e.entry?.car_value && Number(e.entry.car_value) > 0) || (e.amount && Number(e.amount) > 0))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    let runningCosts = 0;
    let baselineValue: number | null = null;
    return displaySorted.map((e, idx) => {
      const amount = e.type !== 'info' && e.amount ? parseFloat(e.amount.toString()) : 0;
      runningCosts += amount;
      const currentTime = new Date(e.date).getTime();
      let estValue: number | null = null;
      if (valPoints.length >= 2) {
        if (currentTime <= valPoints[0].t) estValue = valPoints[0].v;
        else if (currentTime >= valPoints[valPoints.length - 1].t) estValue = valPoints[valPoints.length - 1].v;
        else {
          for (let i = 0; i < valPoints.length - 1; i++) {
            const p1 = valPoints[i]; const p2 = valPoints[i + 1];
            if (currentTime >= p1.t && currentTime <= p2.t) {
              estValue = p1.v + (p2.v - p1.v) * ((currentTime - p1.t) / (p2.t - p1.t));
              break;
            }
          }
        }
      } else if (valPoints.length === 1) estValue = valPoints[0].v;
      if (idx === 0) baselineValue = estValue;
      const depr = (baselineValue !== null && estValue !== null) ? (baselineValue - estValue) : 0;
      return {
        date: currentTime,
        fullDate: new Date(e.date).toLocaleDateString(language, { day: '2-digit', month: 'short', year: 'numeric' }),
        [t.statistics.carMarketValue]: estValue,
        [t.statistics.cumulativeCosts]: runningCosts,
        [t.statistics.totalBurn]: runningCosts + depr,
      };
    });
  }, [filteredExpenses, flatExpenses]);

  const trendPct = prevYearTotal != null && prevYearTotal > 0
    ? ((totalExpenses - prevYearTotal) / prevYearTotal) * 100
    : null;



  const CustomLineTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className="bg-white dark:bg-slate-800 p-4 border border-gray-100 dark:border-white/10 shadow-xl rounded-2xl min-w-[200px]">
          <p className="text-xs font-black text-gray-400 mb-2 uppercase tracking-widest">{d.fullDate}</p>
          <div className="space-y-1.5">
            {payload.map((entry: any) => (
              <div key={entry.name} className="flex items-center justify-between gap-4">
                <span className="text-xs font-bold" style={{ color: entry.color }}>{entry.name}:</span>
                <span className="text-sm font-black text-gray-900 dark:text-white">{fmt(entry.value)}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const ServiceTrendTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-slate-900 p-4 border border-gray-100 dark:border-white/10 shadow-2xl rounded-2xl min-w-[200px]">
          <p className="text-xs font-black text-gray-400 mb-3 uppercase tracking-widest">{label}</p>
          <div className="space-y-3">
            {payload.map((entry: any) => {
              const trend = entry.payload[`${entry.dataKey}Trend`];
              const trendSign = trend > 0 ? '+' : '';
              const trendColor = trend > 0 ? 'text-red-500' : trend < 0 ? 'text-green-500' : 'text-gray-400';
              
              return (
                <div key={entry.name} className="flex flex-col gap-0.5">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                      <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-tight">{entry.name}</span>
                    </div>
                    <span className="text-sm font-black text-gray-900 dark:text-white">
                      {fmt(entry.value)}
                    </span>
                  </div>
                  {trend !== null && (
                    <div className="flex justify-end">
                      <span className={`text-[10px] font-black ${trendColor} uppercase tracking-tighter`}>
                        {trendSign}{trend.toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  const pieData = typeStats.length > 0 ? typeStats : [];
  const activeStat = activePieIndex != null ? typeStats[activePieIndex] : null;

  return (
    <div className="px-3 pb-32 space-y-6 max-w-2xl mx-auto">

      {/* ── KPI CARDS (horizontal scroll) ─────────────────────────────── */}
      <div ref={scrollRef} className="flex gap-3 overflow-x-auto no-scrollbar pb-1 -mx-3 px-3">

        {/* Total */}
        <div className="flex-shrink-0 w-52 bg-gradient-to-br from-blue-500 to-blue-700 rounded-3xl p-5 shadow-lg text-white relative overflow-hidden">
          <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full" />
          <p className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1">
            {selectedYear === 'all' ? t.statistics.totalSpent : t.statistics.spentIn.replace('{year}', selectedYear.toString())}
          </p>
          <p className="text-2xl font-black leading-tight">{fmt(totalExpenses)}</p>
          <p className="text-xs opacity-70 mt-0.5">{filteredExpenses.length} {t.statistics.entries}</p>
          {trendPct != null && !isRestricted && (
            <div className={`flex items-center gap-1 mt-2 text-xs font-bold ${trendPct < 0 ? 'text-green-200' : 'text-red-200'}`}>
              {trendPct < 0 ? <TrendingDown size={14} /> : trendPct > 0 ? <TrendingUp size={14} /> : <Minus size={14} />}
              {Math.abs(trendPct).toFixed(1)}% {trendPct < 0 ? '↓' : '↑'} vs {(selectedYear as number) - 1}
            </div>
          )}
        </div>

        {/* Avg/Month */}
        <div className="flex-shrink-0 w-44 bg-white dark:bg-slate-800 border border-gray-100 dark:border-white/10 rounded-3xl p-5 shadow-sm">
          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-1">{t.expenses.stats.avgMonth}</p>
          <p className="text-xl font-black text-gray-900 dark:text-white leading-tight">{fmt(avgMonthly)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{t.statistics.perMonth}</p>
        </div>

        {/* Cost/km */}
        <div className="flex-shrink-0 w-44 bg-white dark:bg-slate-800 border border-gray-100 dark:border-white/10 rounded-3xl p-5 shadow-sm">
          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-1">{t.expenses.stats.costPer}{distUnit}</p>
          <div className="flex items-center gap-2">
            <p className="text-xl font-black text-gray-900 dark:text-white leading-tight">
              {cpk > 0 ? fmt(cpk) : '—'}
            </p>
            {cpk === 0 && totalExpenses > 0 && !isReadOnly && selectedYear !== 'all' && (
              <button 
                onClick={() => setShowMileageModal(true)}
                className="p-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                title={t.statistics.addMileageData}
              >
                <Plus size={16} />
              </button>
            )}
            {cpk === 0 && totalExpenses > 0 && !isReadOnly && selectedYear === 'all' && (
              <button 
                onClick={() => navigate(`/${selectedCar.slug}/expenses/new`)}
                className="p-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                title={t.statistics.addMileageData}
              >
                <Plus size={16} />
              </button>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {cpk === 0 && totalExpenses > 0 ? (
              <span className="text-[10px] text-orange-500 font-medium block leading-tight mt-1">
                {t.statistics.missingMileageInfo}
              </span>
            ) : t.statistics.overall}
          </p>
        </div>

        {/* Prev year if showing all */}
        {selectedYear === 'all' && yearlyData.length > 0 && yearlyData.slice().reverse().map(yd => (
          <div key={yd.year} className="flex-shrink-0 w-40 bg-white dark:bg-slate-800 border border-gray-100 dark:border-white/10 rounded-3xl p-5 shadow-sm">
            <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-1">{yd.year}</p>
            <p className="text-xl font-black text-gray-900 dark:text-white leading-tight">{fmt(yd.total)}</p>
          </div>
        ))}
      </div>

      {/* ── DONUT CHART ───────────────────────────────────────────────── */}
      {pieData.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-white/10 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">{t.statistics.byType}</h3>
          <div className="relative flex items-center justify-center">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={68}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="total"
                  onMouseEnter={(_, index) => setActivePieIndex(index)}
                  onMouseLeave={() => setActivePieIndex(null)}
                  onClick={(_, index) => setExpandedType(typeStats[index]?.type === expandedType ? null : typeStats[index]?.type)}
                >
                  {pieData.map((entry, index) => {
                    const isGroupActive = activeStat?.isService && entry.isService;
                    const isIndividualActive = activePieIndex === index;
                    const isActive = isGroupActive || isIndividualActive;

                    return (
                      <Cell
                        key={entry.type}
                        fill={entry.color}
                        opacity={activePieIndex == null || isActive ? 1 : 0.25}
                        stroke={entry.isService ? '#EF4444' : 'none'}
                        strokeWidth={entry.isService ? 1.5 : 0}
                        className="transition-all duration-300"
                      />
                    );
                  })}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            {/* Fixed Tooltip Overlay (Top Right) */}
            {activeStat && (
              <div className="absolute top-0 right-0 z-10 w-44 animate-in fade-in slide-in-from-top-2 duration-200 pointer-events-none">
                <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-md px-3 py-3 rounded-2xl shadow-xl border border-gray-100 dark:border-white/10 text-xs">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${activeStat.color}20` }}>
                      <DynamicIcon name={activeStat.icon} size={12} color={activeStat.color} />
                    </div>
                    <p className="font-black text-gray-900 dark:text-white truncate">{getTypeName(activeStat.type)}</p>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex justify-between items-baseline">
                      <span className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">{(t.expenses as any).amount || (language === 'lv' ? 'Summa' : 'Amount')}</span>
                      <span className="font-black text-gray-900 dark:text-white">{fmt(activeStat.total)}</span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">{language === 'lv' ? 'Daļa' : 'Share'}</span>
                      <span className="text-gray-500 dark:text-gray-400 font-bold">{activeStat.percentage.toFixed(1)}%</span>
                    </div>
                  </div>

                  {activeStat.isService && (
                    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-white/10">
                      <p className="text-[9px] font-black uppercase tracking-widest text-red-500 mb-1">
                        {((t.expenses.typeLabels as any)?.service || (language === 'lv' ? 'Serviss' : 'Service'))} {language === 'lv' ? 'Kopā' : 'Total'}
                      </p>
                      <div className="flex justify-between items-baseline">
                        <span className="font-bold text-gray-900 dark:text-white">{fmt(activeStat.groupTotal)}</span>
                        <span className="text-gray-500 font-bold">{activeStat.groupPercentage.toFixed(1)}%</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Centre label */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              {activeStat ? (
                <div className="text-center px-2">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center mx-auto mb-1" style={{ backgroundColor: `${activeStat.isService ? '#EF4444' : activeStat.color}20` }}>
                    <DynamicIcon name={activeStat.isService ? 'Wrench' : activeStat.icon} size={16} color={activeStat.isService ? '#EF4444' : activeStat.color} />
                  </div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">
                    {(activeStat as any).groupName}
                  </p>
                  <p className="text-lg font-black text-gray-900 dark:text-white">{fmt((activeStat as any).groupTotal)}</p>
                  <p className="text-xs font-bold text-gray-400">{(activeStat as any).groupPercentage.toFixed(1)}%</p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-lg font-black text-gray-900 dark:text-white">{fmt(totalExpenses)}</p>
                  <p className="text-xs text-gray-400 font-medium">{t.statistics.totalSpent}</p>
                </div>
              )}
            </div>
          </div>

          {/* Legend dots */}
          <div className="flex flex-wrap gap-x-4 gap-y-2 justify-center mt-2">
            {pieData.map(s => (
              <button
                key={s.type}
                type="button"
                onClick={() => setExpandedType(s.type === expandedType ? null : s.type)}
                className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:opacity-80 transition-opacity"
              >
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                {getTypeName(s.type)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── CATEGORY CARDS ────────────────────────────────────────────── */}
      <div className="space-y-2">
        {typeStats.map(stat => {
          const isExpanded = expandedType === stat.type;
          return (
            <div
              key={stat.type}
              className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm"
            >
              <button
                type="button"
                onClick={() => setExpandedType(isExpanded ? null : stat.type)}
                className="w-full text-left p-4 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              >
                <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: `${stat.color}18` }}>
                  <DynamicIcon name={stat.icon} size={20} color={stat.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline gap-2">
                    <span className="font-bold text-gray-800 dark:text-gray-100 truncate">{getTypeName(stat.type)}</span>
                    <span className="font-black text-gray-900 dark:text-white flex-shrink-0">{fmt(stat.total)}</span>
                  </div>
                  {/* Progress bar */}
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex-1 h-1.5 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${stat.percentage}%`, backgroundColor: stat.color }} />
                    </div>
                    <span className="text-xs text-gray-400 font-bold w-9 text-right">{stat.percentage.toFixed(0)}%</span>
                    <span className="text-xs text-gray-400">({stat.count})</span>
                  </div>
                </div>
                {isExpanded ? <ChevronDown size={16} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />}
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 space-y-3 border-t border-gray-50 dark:border-white/5 pt-3">
                  {isRestricted ? (
                    <div className="py-2">
                       <Link to="/settings/plans" className="flex items-center justify-between p-3 bg-red-50/50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-xl group transition-all">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center shrink-0">
                               <Rocket size={16} />
                            </div>
                            <div>
                               <p className="text-[11px] font-bold text-red-800 dark:text-red-300 uppercase tracking-widest leading-none mb-0.5">
                                 {(t.statistics as any).proAnalysis || 'Pro analīze'}
                               </p>
                               <p className="text-[10px] font-medium text-red-600 dark:text-red-400 opacity-80 leading-tight">
                                 {(t.statistics as any).detailedAnalysis || 'Detalizēta analīze'}
                               </p>
                            </div>
                          </div>
                          <span className="text-[10px] font-black text-red-700 dark:text-red-300 group-hover:translate-x-1 transition-transform">
                             {(t.statistics as any).upgradeToSee || 'Uzlabot →'}
                          </span>
                       </Link>
                    </div>
                  ) : (
                    <>
                      {stat.type === 'fuel' && (stat.totalLiters || 0) > 0 && (
                        <div className="space-y-1.5 px-1">
                          <p className="text-xs text-gray-500 font-medium flex justify-between">
                            <span>{effectivePreferences?.volume_unit === 'gallons' ? t.settings.gallons : t.settings.liters}:</span>
                            <strong className="text-gray-900 dark:text-gray-100">{stat.totalLiters?.toFixed(2)} {effectivePreferences?.volume_unit === 'gallons' ? 'gal' : 'L'}</strong>
                          </p>
                          {stat.avgUnitPrice && (
                            <p className="text-xs text-gray-500 font-medium flex justify-between">
                              <span>{t.statistics.avgPrice || 'Vidējā cena'}:</span>
                              <span className="text-gray-900 dark:text-gray-100 font-bold">
                                {stat.avgUnitPrice.toFixed(3)} {effectivePreferences?.currency_symbol || '€'}/{effectivePreferences?.volume_unit === 'gallons' ? 'gal' : 'L'}
                                <span className="text-[10px] text-gray-400 font-normal ml-1">({stat.minUnitPrice?.toFixed(3)} - {stat.maxUnitPrice?.toFixed(3)})</span>
                              </span>
                            </p>
                          )}
                          {distance > 0 && (
                            <p className="text-xs text-gray-500 font-medium flex justify-between">
                              <span>{t.statistics.consumption || 'Patēriņš'}:</span>
                              <strong className="text-gray-900 dark:text-gray-100">{((stat.totalLiters || 0) / (distance / 100)).toFixed(1)} L/100{effectivePreferences?.distance_unit === 'miles' ? 'mi' : 'km'}</strong>
                            </p>
                          )}
                        </div>
                      )}
                      {stat.type === 'electricity' && (stat.totalElectricityKwh || 0) > 0 && (
                        <div className="space-y-1.5 px-1">
                          <p className="text-xs text-gray-500 font-medium flex justify-between">
                            <span>kWh:</span>
                            <strong className="text-gray-900 dark:text-white">{stat.totalElectricityKwh?.toFixed(2)} kWh</strong>
                          </p>
                          {stat.avgUnitPrice && (
                            <p className="text-xs text-gray-500 font-medium flex justify-between">
                              <span>{t.statistics.avgPrice || 'Vidējā cena'}:</span>
                              <span className="text-gray-900 dark:text-gray-100 font-bold">
                                {stat.avgUnitPrice.toFixed(3)} {effectivePreferences?.currency_symbol || '€'}/kWh
                                <span className="text-[10px] text-gray-400 font-normal ml-1">({stat.minUnitPrice?.toFixed(3)} - {stat.maxUnitPrice?.toFixed(3)})</span>
                              </span>
                            </p>
                          )}
                        </div>
                      )}
                      {stat.topEntries.length > 0 && (
                        <div className="pt-2">
                          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3 px-1">{t.statistics.mostExpensive}</p>
                          <div className="bg-gray-50/50 dark:bg-white/5 rounded-2xl overflow-hidden divide-y divide-gray-100 dark:divide-white/5 border border-gray-100 dark:border-white/5">
                            {stat.topEntries.map(entry => {
                              const meta = stat.entryMetadata[entry.id];
                              return (
                                <LogEntryItem
                                  key={entry.id}
                                  entry={entry}
                                  slug={selectedCar?.slug || ''}
                                  effectivePreferences={effectivePreferences}
                                  amountOverride={meta?.amount}
                                  descriptionOverride={meta?.description}
                                  forceShowYear={true}
                                />
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── MONTHLY / YEARLY BAR SECTION ─────────────────────────────── */}
      {monthlyData.length > 1 && (
        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-white/10 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">
            {selectedYear === 'all' ? t.statistics.byYear : t.statistics.byMonth}
          </h3>
          <div className="space-y-2">
            {(selectedYear !== 'all' ? monthlyData : yearlyData.slice().reverse()).map((item: any) => {
              const val = item.total;
              const max = Math.max(...(selectedYear !== 'all' ? monthlyData : yearlyData).map((x: any) => x.total));
              const pct = max > 0 ? (val / max) * 100 : 0;
              return (
                <div key={item.label || item.year} className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 w-20 flex-shrink-0 capitalize">{item.label || item.year}</span>
                  <div className="flex-1 h-2 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-bold text-gray-700 dark:text-gray-200 w-24 text-right">{fmt(val)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── ASSET VALUE & COST CHART ──────────────────────────────────── */}
      {chartData.length >= 2 && (
        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-white/10 shadow-sm p-5 overflow-hidden">
          <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">{t.statistics.assetAnalysis}</h3>
          {isRestricted ? (
             <ChartTeaser title={t.statistics.assetAnalysis} />
          ) : (
            <>
              <div className="h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148,163,184,0.2)" />
                    <XAxis
                      dataKey="date"
                      type="number"
                      domain={['dataMin', 'dataMax']}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
                      tickFormatter={val => new Date(val).toLocaleDateString(language, {
                        month: 'short',
                        year: selectedYear === 'all' ? '2-digit' : undefined
                      })}
                      dy={8}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
                      tickFormatter={val => {
                        const sym = effectivePreferences?.currency_symbol || '€';
                        const f = `${(val / 1000).toFixed(0)}k`;
                        const pre = ['USD', 'CAD', 'AUD', 'JPY'].includes(effectivePreferences?.currency);
                        return pre ? `${sym}${f}` : `${f}${sym}`;
                      }}
                    />
                    <Tooltip content={<CustomLineTooltip />} />
                    <Legend verticalAlign="top" align="center" wrapperStyle={{ paddingBottom: 16, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }} />
                    <Line type="monotone" dataKey={t.statistics.carMarketValue} stroke="#3b82f6" strokeWidth={3} dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
                    <Line type="monotone" dataKey={t.statistics.cumulativeCosts} stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
                    <Line type="monotone" dataKey={t.statistics.totalBurn} stroke="#ef4444" strokeWidth={3} dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-4 text-[11px] text-gray-400 font-medium italic text-center">{t.statistics.burnNote}</p>
            </>
          )}
        </div>
      )}

      {/* ── FUEL PRICE HISTORY CHART ─────────────────────────────────── */}
      {fuelPriceData.length >= 2 && (
        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-white/10 shadow-sm p-5 overflow-hidden">
          <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">
            {t.statistics.fuelPriceTrend || 'Degvielas cenas tendence'} ({effectivePreferences?.currency_symbol || '€'}/{effectivePreferences?.volume_unit === 'gallons' ? 'gal' : 'L'})
          </h3>
          {isRestricted ? (
             <ChartTeaser title={t.statistics.fuelPriceTrend} />
          ) : (
            <div className="h-[280px] w-full pt-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={fuelPriceData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148,163,184,0.1)" />
                  <XAxis
                    dataKey="date"
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
                    dy={10}
                    tickFormatter={val => new Date(val).toLocaleDateString(language, {
                      month: 'short',
                      year: '2-digit'
                    })}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
                    domain={['auto', 'auto']}
                    dx={-5}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#000D19', border: 'none', borderRadius: '12px', fontSize: '12px', color: '#fff', padding: '8px 12px' }}
                    itemStyle={{ color: '#60A5FA', fontWeight: 'bold' }}
                    labelStyle={{ color: '#94a3b8', marginBottom: '4px' }}
                    labelFormatter={(label, payload) => {
                      if (payload && payload.length > 0) {
                        return payload[0].payload.fullDate;
                      }
                      return label;
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke="#3B82F6"
                    strokeWidth={3}
                    dot={{ r: 4, fill: '#3B82F6', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 6, fill: '#3B82F6', strokeWidth: 0 }}
                    animationDuration={1500}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* ── MILEAGE HISTORY CHART ─────────────────────────────────── */}
      {mileageData.length >= 2 && (
        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-white/10 shadow-sm p-5 overflow-hidden">
          <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">
            {t.statistics.mileageTrend} ({distUnit})
          </h3>
          {isRestricted ? (
             <ChartTeaser title={t.statistics.mileageTrend} />
          ) : (
            <div className="h-[280px] w-full pt-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={mileageData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148,163,184,0.1)" />
                  <XAxis
                    dataKey="date"
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
                    dy={10}
                    tickFormatter={val => new Date(val).toLocaleDateString(language, {
                      month: 'short',
                      year: '2-digit'
                    })}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
                    domain={['auto', 'auto']}
                    dx={-5}
                    tickFormatter={val => `${val.toLocaleString()}`}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#000D19', border: 'none', borderRadius: '12px', fontSize: '12px', color: '#fff', padding: '8px 12px' }}
                    itemStyle={{ color: '#10B981', fontWeight: 'bold' }}
                    labelStyle={{ color: '#94a3b8', marginBottom: '4px' }}
                    labelFormatter={(label, payload) => {
                      if (payload && payload.length > 0) {
                        return payload[0].payload.fullDate;
                      }
                      return label;
                    }}
                    formatter={(value: any) => [value.toLocaleString(), distUnit]}
                  />
                  <Line
                    type="monotone"
                    dataKey="mileage"
                    name={distUnit.toUpperCase()}
                    stroke="#10B981"
                    strokeWidth={3}
                    dot={{ r: 4, fill: '#10B981', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 6, fill: '#10B981', strokeWidth: 0 }}
                    animationDuration={1500}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* ── YEARLY CPK CHART ─────────────────────────────────── */}
      {selectedYear === 'all' && yearlyCpkData.length >= 2 && (
        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-white/10 shadow-sm p-5 overflow-hidden">
          <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">
            {((t.statistics as any).costPerKmTrend || `Izmaksas uz 1 {unit}`).replace('{unit}', effectivePreferences?.distance_unit === 'miles' ? (t.common?.milesShort || 'mi') : (t.common?.kmShort || 'km'))} ({effectivePreferences?.currency_symbol || '€'}/{effectivePreferences?.distance_unit === 'miles' ? (t.common?.milesShort || 'mi') : (t.common?.kmShort || 'km')})
          </h3>
          {isRestricted ? (
             <ChartTeaser title={(t.statistics as any).costPerKmTrend?.replace('{unit}', effectivePreferences?.distance_unit === 'miles' ? (t.common?.milesShort || 'mi') : (t.common?.kmShort || 'km'))} />
          ) : (
            <div className="h-[280px] w-full pt-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={yearlyCpkData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148,163,184,0.1)" />
                  <XAxis
                    dataKey="yearNum"
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
                    dy={10}
                    tickFormatter={val => val.toString()}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
                    domain={[0, 'auto']}
                    dx={-5}
                    tickFormatter={val => val.toFixed(2)}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#000D19', border: 'none', borderRadius: '12px', fontSize: '12px', color: '#fff', padding: '8px 12px' }}
                    itemStyle={{ color: '#F59E0B', fontWeight: 'bold' }}
                    labelStyle={{ color: '#94a3b8', marginBottom: '4px' }}
                    formatter={(value: any) => [`${value.toFixed(3)} ${effectivePreferences?.currency_symbol || '€'}`, (t.statistics as any).costPerKm || `Izmaksas/${distUnit}`]}
                  />
                  <Line
                    type="monotone"
                    dataKey="cpk"
                    name={(t.statistics as any).costPerKm || `Izmaksas/${distUnit}`}
                    stroke="#F59E0B"
                    strokeWidth={3}
                    dot={{ r: 4, fill: '#F59E0B', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 6, fill: '#F59E0B', strokeWidth: 0 }}
                    animationDuration={1500}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* ── SERVICE TRENDS CHART ────────────────────────────────────── */}
      {selectedYear === 'all' && serviceTrendData.length >= 2 && (
        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-white/10 shadow-sm p-5 overflow-hidden">
          <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">
            {t.statistics.serviceTrendsTitle}
          </h3>
          {isRestricted ? (
            <ChartTeaser title={t.statistics.serviceTrendsTitle} />
          ) : (
            <div className="h-[280px] w-full pt-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={serviceTrendData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148,163,184,0.1)" />
                  <XAxis
                    dataKey="year"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 600, fill: '#94a3b8' }}
                    domain={[0, 'auto']}
                    dx={-5}
                    tickFormatter={val => {
                        const sym = effectivePreferences?.currency_symbol || '€';
                        if (val >= 1000) return `${(val / 1000).toFixed(1)}k${sym}`;
                        return `${val}${sym}`;
                    }}
                  />
                  <Tooltip content={<ServiceTrendTooltip />} />
                  <Legend verticalAlign="top" align="center" wrapperStyle={{ paddingBottom: 16, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }} />
                  <Line
                    type="monotone"
                    dataKey="repairs"
                    name={getTypeName('service_repair')}
                    stroke="#EF4444"
                    strokeWidth={3}
                    dot={{ r: 4, fill: '#EF4444', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 6, fill: '#EF4444', strokeWidth: 0 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="maintenance"
                    name={getTypeName('service_maintenance')}
                    stroke="#34D399"
                    strokeWidth={3}
                    dot={{ r: 4, fill: '#34D399', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 6, fill: '#34D399', strokeWidth: 0 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="upgrade"
                    name={getTypeName('service_upgrade')}
                    stroke="#8B5CF6"
                    strokeWidth={3}
                    dot={{ r: 4, fill: '#8B5CF6', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 6, fill: '#8B5CF6', strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Feedback Link */}
      <div className="mt-12 mb-8 text-center pb-8 border-t border-gray-100 dark:border-white/5 pt-8">
        <p className="text-sm font-medium text-gray-400">
          Ideas for different stats / graphs - {' '}
          <button 
            onClick={() => navigate('/contact')} 
            className="text-blue-500 hover:text-blue-600 transition-colors font-bold"
          >
            let us know!
          </button>
        </p>
      </div>

      {/* Missing Mileage Modal */}
      {selectedYear !== 'all' && (
        <MissingMileageModal
          isOpen={showMileageModal}
          onClose={() => setShowMileageModal(false)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: LOG_ENTRY_KEYS.list(selectedCar.id) })}
          carId={selectedCar.id}
          userId={selectedCar.user_id}
          selectedYear={selectedYear as number}
          logEntries={logEntries}
          t={t}
          language={language}
          purchaseDate={purchaseInfo && (selectedYear as number) === purchaseInfo.year ? purchaseInfo.date : undefined}
          minAllowedYear={purchaseInfo?.year}
        />
      )}
    </div>
  );
}
